import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
  type QueryKey,
} from '@tanstack/react-query';
import service from '../service';
import type { FlowInstance } from '../types/flow';

type FlowInstanceResponse = FlowInstance;
type DefaultError = unknown;

type UseFlowInstanceParams = {
  flowInstanceId?: string;
  /** Override default enabled behavior */
  enabled?: boolean;
};

/**
 * useFlowInstance
 *
 * @example
 * const flowInstanceQuery = useFlowInstance(
 *   {
 *     flowInstanceId,
 *   },
 *   {
 *     staleTime: 60_000,
 *   },
 * );
 */
export function useFlowInstance<
  TData = FlowInstanceResponse,
  TError = DefaultError,
>(
  { flowInstanceId, enabled = true }: UseFlowInstanceParams = {},
  options?: Omit<
    UseQueryOptions<FlowInstanceResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  const queryKey: QueryKey = ['flowInstance', flowInstanceId];

  return useQuery<FlowInstanceResponse, TError, TData>({
    queryKey,
    enabled: enabled && !!flowInstanceId,
    queryFn: async () => {
      if (!flowInstanceId) {
        throw new Error('flowInstanceId is required to fetch a flow instance');
      }

      return service.getFlowInstance(flowInstanceId);
    },
    ...(options as any),
  });
}
